/**
 * EditLotDialog Component
 * Dialog for editing lot/batch details - matches CapsInventory UI
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Box, Typography,
  IconButton, CircularProgress, Alert, alpha, useTheme, Paper,
  Tooltip, InputAdornment, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import {
  Close as CloseIcon, CloudUpload as UploadIcon, Edit as EditIcon,
  Delete as DeleteIcon, DeleteOutline as DeleteOutlineIcon,
  Receipt as ReceiptIcon, Description as DescriptionIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon, Science as ScienceIcon,
  LocalShipping as LocalShippingIcon, Search as SearchIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { getInventoryConfig } from './inventoryConfig';
import ConfigSelectionDialog from '../Add/AddInventoryStepper/ConfigSelectionDialog';

export default function EditLotDialog({
  open,
  onClose,
  item,
  config,
  inventoryType,
  onSuccess,
  canDelete = true
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [quantityMode, setQuantityMode] = useState('container'); // 'container' | 'measure' (chemicals only)
  
  // Unit config dialog state (products only)
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configSearch, setConfigSearch] = useState('');
  const [availableConfigs, setAvailableConfigs] = useState([]);

  // Document states
  const [selectedFiles, setSelectedFiles] = useState({
    invoice: null,
    po: null,
    coa: null,
    sds: null,
    msds: null,
    bol: null,
    production_doc: null
  });
  const [filesToDelete, setFilesToDelete] = useState({
    invoice: false,
    po: false,
    coa: false,
    sds: false,
    msds: false,
    bol: false,
    production_doc: false
  });
  const [uploadingFiles, setUploadingFiles] = useState({
    invoice: false,
    po: false,
    coa: false,
    sds: false,
    msds: false,
    bol: false,
    production_doc: false
  });
  
  // Use passed config or get from inventoryType if not provided
  const finalConfig = config || (inventoryType ? getInventoryConfig(inventoryType) : null);
  const lot = item;
  const companyId = lot?.company_id || lot?.location?.company_id;
  // Use config-defined quantity field (chemicals use current_unit_quantity, others use current_quantity)
  const qField = finalConfig?.quantityField || 'current_quantity';
  const isProduct = finalConfig?.hasUnitConfig;
  const isChemical = finalConfig?.type === 'chemicals';

  // Initialize form data when lot changes (NOT when suppliers change, to avoid resetting user edits)
  useEffect(() => {
    if (lot) {
      setQuantityMode('container');
      // Clean system-generated notes before showing in form
      const rawNotes = lot.notes || '';
      const cleanedNotes = rawNotes.split(';').map(s => s.trim()).filter(seg => {
        if (!seg) return false;
        if (seg.match(/^Adjusted to \d+/)) return false;
        if (seg.startsWith('Transfer merge - Added')) return false;
        if (seg.startsWith('Transfer from location - Ref:')) return false;
        if (seg.startsWith('Adjustment created lot/batch - Ref:')) return false;
        if (seg.match(/^Merged transfer from /)) return false;
        return true;
      }).join('; ');

      setFormData({
        current_quantity: lot[qField] || 0,
        current_measure_quantity: lot.current_total_quantity || 0,
        lot_number: lot.lot_number || '',
        batch_number: lot.batch_number || '',
        notes: cleanedNotes,
        supplier_id: lot.source_transaction?.supplier_id || '',
        po_number: lot.source_transaction?.po_number || '',
        po_date: lot.source_transaction?.po_date || '',
        invoice_number: lot.source_transaction?.invoice_number || '',
        invoice_date: lot.source_transaction?.invoice_date || '',
        expiry_date: lot.expiry_date || '',
        // Product-specific fields
        ...(isProduct ? {
          pallet_quantity: lot.pallet_quantity || 0,
          case_quantity: lot.case_quantity || 0,
          unit_quantity: lot.unit_quantity || 0,
          manufactured_date: lot.manufactured_date || '',
          // Unit config (editable)
          units_per_case: lot.units_per_case || 1,
          cases_per_pallet: lot.cases_per_pallet || 1,
          enable_cases: lot.enable_cases ?? true,
          enable_pallets: lot.enable_pallets ?? false,
          _configName: null // populated when user picks a new config
        } : {})
      });
    }
  }, [lot]);

  // Match selected vendor when suppliers load (separate to avoid resetting form edits)
  useEffect(() => {
    if (lot?.source_transaction?.supplier_id && suppliers.length > 0) {
      const vendor = suppliers.find(s => s.id === lot.source_transaction.supplier_id);
      setSelectedVendor(vendor || null);
    }
  }, [lot, suppliers]);
  
  // Fetch suppliers
  useEffect(() => {
    if (open && companyId) {
      fetchSuppliers();
    }
  }, [open, companyId]);

  // Early return check AFTER all hooks
  if (!finalConfig) {
    console.error('No config available for EditLotDialog');
    return null;
  }
  
  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setFormData(prev => ({
      ...prev,
      supplier_id: vendor.id
    }));
    setVendorDialogOpen(false);
    setVendorSearch('');
  };
  
  const openConfigDialog = async () => {
    setConfigSearch('');
    setConfigDialogOpen(true);
    const { data } = await supabase
      .from('unit_configurations')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name');
    setAvailableConfigs(data || []);
  };

  const handleConfigSelect = (cfg) => {
    const newEnablePallets = cfg.enable_pallets ?? false;
    const newEnableCases = cfg.enable_cases ?? true;
    setFormData(prev => ({
      ...prev,
      units_per_case: cfg.units_per_case || 1,
      cases_per_pallet: cfg.cases_per_pallet || 1,
      enable_cases: newEnableCases,
      enable_pallets: newEnablePallets,
      // Zero out quantities for disabled tiers so they don't silently contribute to total
      pallet_quantity: newEnablePallets ? prev.pallet_quantity : 0,
      case_quantity: newEnableCases ? prev.case_quantity : 0,
      _configName: cfg.name,
      _configId: cfg.id
    }));
    setConfigDialogOpen(false);
  };

  const handleFileSelect = (event, fileType) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
      // Cancel any pending delete for this file type
      setFilesToDelete(prev => ({
        ...prev,
        [fileType]: false
      }));
    }
  };
  
  const removeSelectedFile = (fileType) => {
    setSelectedFiles(prev => ({
      ...prev,
      [fileType]: null
    }));
  };
  
  const handleDeleteDocument = (fileType) => {
    setFilesToDelete(prev => ({
      ...prev,
      [fileType]: true
    }));
  };
  
  const handleCancelDeleteDocument = (fileType) => {
    setFilesToDelete(prev => ({
      ...prev,
      [fileType]: false
    }));
  };
  
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Products use pallet/case/unit fields; others use single quantity
      let newQuantity, oldQuantity, quantityChanged, newChemTotalQty;
      if (isProduct) {
        const _enablePallets = formData.enable_pallets ?? false;
        const _enableCases = formData.enable_cases ?? true;
        const pQty = _enablePallets ? (parseInt(formData.pallet_quantity) || 0) : 0;
        const cQty = _enableCases ? (parseInt(formData.case_quantity) || 0) : 0;
        const uQty = parseInt(formData.unit_quantity) || 0;
        const upc = formData.units_per_case || lot.units_per_case || 1;
        const cpp = formData.cases_per_pallet || lot.cases_per_pallet || 1;
        newQuantity = (pQty * cpp * upc) + (cQty * upc) + uQty;
        oldQuantity = lot.total_base_units || 0;
        quantityChanged = pQty !== (lot.pallet_quantity || 0) ||
                          cQty !== (lot.case_quantity || 0) ||
                          uQty !== (lot.unit_quantity || 0);
      } else if (isChemical) {
        const chemUnitQtyPerUnit = lot._unitQtyPerUnit ? parseFloat(lot._unitQtyPerUnit) : 1;
        if (quantityMode === 'measure') {
          const measureQty = parseFloat(formData.current_measure_quantity) || 0;
          newQuantity = parseFloat((measureQty / chemUnitQtyPerUnit).toFixed(4));
          newChemTotalQty = parseFloat(measureQty.toFixed(4));
        } else {
          newQuantity = parseFloat(formData.current_quantity) || 0;
          newChemTotalQty = parseFloat((newQuantity * chemUnitQtyPerUnit).toFixed(4));
        }
        oldQuantity = lot[qField] || 0;
        quantityChanged = newQuantity !== oldQuantity || newChemTotalQty !== (lot.current_total_quantity || 0);
      } else {
        newQuantity = parseFloat(formData.current_quantity) || 0;
        oldQuantity = lot[qField] || 0;
        quantityChanged = newQuantity !== oldQuantity;
      }

      // Validate quantity
      if (newQuantity < 0) {
        throw new Error('Quantity cannot be negative');
      }

      // Update inventory item - ALL fields including quantity
      let itemUpdate;
      if (isProduct) {
        const enablePallets = formData.enable_pallets ?? false;
        const enableCases = formData.enable_cases ?? true;
        itemUpdate = {
          pallet_quantity: enablePallets ? (parseInt(formData.pallet_quantity) || 0) : 0,
          case_quantity: enableCases ? (parseInt(formData.case_quantity) || 0) : 0,
          unit_quantity: parseInt(formData.unit_quantity) || 0,
          units_per_case: formData.units_per_case || 1,
          cases_per_pallet: formData.cases_per_pallet || 1,
          enable_cases: formData.enable_cases ?? true,
          enable_pallets: formData.enable_pallets ?? false,
          lot_number: formData.lot_number || null,
          batch_number: formData.batch_number || null,
          notes: formData.notes || null,
          is_active: newQuantity > 0,
          last_updated_at: new Date().toISOString(),
          expiry_date: formData.expiry_date || null,
          manufactured_date: formData.manufactured_date || null
        };
      } else {
        itemUpdate = {
          [qField]: newQuantity,
          ...(isChemical && newChemTotalQty !== undefined ? { current_total_quantity: newChemTotalQty } : {}),
          lot_number: formData.lot_number || null,
          batch_number: formData.batch_number || null,
          notes: formData.notes || null,
          is_active: newQuantity > 0,
          last_updated_at: new Date().toISOString(),
          ...(finalConfig?.hasUnitOfMeasure ? { expiry_date: formData.expiry_date || null } : {})
        };
      }

      const { error: itemError } = await supabase
        .from(finalConfig.itemsTable)
        .update(itemUpdate)
        .eq('id', lot.id);

      if (itemError) throw itemError;

      // Update source transaction (vendor, PO, invoice details)
      // If no source_transaction_id exists, create one so we can store PO/invoice data
      let transactionId = lot.source_transaction_id;

      const transactionUpdate = {
        supplier_id: formData.supplier_id || null,
        po_number: formData.po_number || null,
        po_date: formData.po_date || null,
        invoice_number: formData.invoice_number || null,
        invoice_date: formData.invoice_date || null
      };

      if (transactionId) {
        const { error: txError } = await supabase
          .from(finalConfig.transactionsTable)
          .update(transactionUpdate)
          .eq('id', transactionId);

        if (txError) throw txError;
      } else {
        // Create a new transaction record to link with this lot
        // For products, production_doc is saved directly to items — don't count it for tx creation
        const hasAnyTxData = formData.supplier_id || formData.po_number || formData.invoice_number ||
          Object.entries(selectedFiles).some(([key, f]) => f && !(isProduct && key === 'production_doc'));

        if (hasAnyTxData) {
          const newTxData = {
            company_id: companyId,
            [finalConfig.itemIdField]: lot[finalConfig.itemIdField],
            ...(isProduct ? { variant_id: lot.variant_id || null } : {}),
            transaction_type: 'inventory_creation',
            quantity_change: isProduct ? (lot.total_base_units || newQuantity) : newQuantity,
            ...(isProduct ? { to_location_id: lot.location_id } : { location_id: lot.location_id }),
            lot_number: formData.lot_number || null,
            batch_number: formData.batch_number || null,
            ...transactionUpdate,
            created_at: lot.created_at || new Date().toISOString(),
            notes: 'Transaction created via lot edit'
          };

          const { data: newTx, error: txCreateError } = await supabase
            .from(finalConfig.transactionsTable)
            .insert(newTxData)
            .select()
            .single();

          if (txCreateError) throw txCreateError;
          transactionId = newTx.id;

          // Link the new transaction to the lot item
          await supabase
            .from(finalConfig.itemsTable)
            .update({ source_transaction_id: transactionId })
            .eq('id', lot.id);
        }
      }

      // For products, handle production_doc and bol directly on items table (no transaction needed)
      if (isProduct) {
        if (selectedFiles.production_doc) {
          await uploadFile('production_doc', selectedFiles.production_doc, lot.id);
        }
        if (filesToDelete.production_doc) {
          await deleteFile('production_doc', lot.id);
        }
        if (selectedFiles.bol) {
          await uploadFile('bol', selectedFiles.bol, lot.id);
        }
        if (filesToDelete.bol) {
          await deleteFile('bol', lot.id);
        }
      }

      if (transactionId) {
        // Handle file uploads (skip production_doc and bol for products — handled above)
        for (const [fileType, file] of Object.entries(selectedFiles)) {
          if (file && !(isProduct && (fileType === 'production_doc' || fileType === 'bol'))) {
            await uploadFile(fileType, file, transactionId);
          }
        }

        // Handle file deletions (skip production_doc and bol for products — handled above)
        for (const [fileType, shouldDelete] of Object.entries(filesToDelete)) {
          if (shouldDelete && !(isProduct && (fileType === 'production_doc' || fileType === 'bol'))) {
            await deleteFile(fileType, transactionId);
          }
        }

        // For products, sync newly-uploaded transaction-level doc paths to the items table.
        // Only sync fields that were actually just uploaded — never overwrite with null.
        if (isProduct && Object.values(selectedFiles).some(f => f && f !== selectedFiles.production_doc && f !== selectedFiles.bol)) {
          const txDocTypes = {
            invoice: { path: 'invoice_file_path', name: 'invoice_file_name' },
            po:      { path: 'po_file_path',      name: 'po_file_name' },
            coa:     { path: 'coa_file_path',      name: 'coa_file_name' },
            sds:     { path: 'sds_file_path',      name: 'sds_file_name' }
          };

          const { data: updatedTx } = await supabase
            .from(finalConfig.transactionsTable)
            .select('*')
            .eq('id', transactionId)
            .single();

          if (updatedTx) {
            const itemDocUpdate = {};
            // Only copy fields that were just uploaded in this session (selectedFiles has the file)
            for (const [key, cols] of Object.entries(txDocTypes)) {
              if (selectedFiles[key] && updatedTx[cols.path]) {
                itemDocUpdate[cols.path] = updatedTx[cols.path];
                itemDocUpdate[cols.name] = updatedTx[cols.name] || null;
              }
            }
            if (Object.keys(itemDocUpdate).length > 0) {
              await supabase
                .from(finalConfig.itemsTable)
                .update(itemDocUpdate)
                .eq('id', lot.id);
            }
          }
        }
      }

      // Log adjustment transaction for audit trail if quantity changed
      if (quantityChanged) {
        const itemName = lot[finalConfig.tableName]?.name || lot.item_name || 'item';
        const adjNotes = `Lot edited — ${itemName}\n• Qty: ${oldQuantity} → ${newQuantity}`;

        if (isProduct) {
          const { error: txLogError } = await supabase
            .from(finalConfig.transactionsTable)
            .insert({
              company_id: companyId,
              [finalConfig.itemIdField]: lot[finalConfig.itemIdField],
              variant_id: lot.variant_id || null,
              transaction_type: 'adjustment',
              quantity_change: newQuantity - oldQuantity,
              to_location_id: lot.location_id,
              lot_number: formData.lot_number || lot.lot_number || null,
              batch_number: formData.batch_number || lot.batch_number || null,
              units_per_case: formData.units_per_case || lot.units_per_case || 1,
              cases_per_pallet: formData.cases_per_pallet || lot.cases_per_pallet || 1,
              created_at: new Date().toISOString(),
              notes: adjNotes
            });
          if (txLogError) {
            console.error('Failed to log adjustment transaction:', txLogError);
          }
        } else {
          const { error: txLogError } = await supabase
            .from(finalConfig.transactionsTable)
            .insert({
              company_id: companyId,
              [finalConfig.itemIdField]: lot[finalConfig.itemIdField],
              transaction_type: 'adjustment',
              quantity: newQuantity,
              location_id: lot.location_id,
              lot_number: formData.lot_number || lot.lot_number || null,
              batch_number: formData.batch_number || lot.batch_number || null,
              transaction_date: new Date().toISOString(),
              notes: adjNotes
            });
          if (txLogError) {
            console.error('Failed to log adjustment transaction:', txLogError);
          }
        }
      }

      // Recalculate aggregate inventory
      if (isProduct) {
        // Products use RPC for aggregate recalculation
        await supabase.rpc('recalculate_product_inventory_aggregate', {
          p_company_id:  companyId,
          p_product_id:  lot[finalConfig.itemIdField],
          p_variant_id:  lot.variant_id || null,
          p_location_id: lot.location_id
        });
      } else if (isChemical) {
        await supabase.rpc('recalculate_raw_materials_aggregate_inventory', {
          p_company_id:      companyId,
          p_raw_material_id: lot[finalConfig.itemIdField],
          p_location_id:     lot.location_id
        });
      } else {
        const { data: remainingItems } = await supabase
          .from(finalConfig.itemsTable)
          .select(qField)
          .eq('company_id', companyId)
          .eq(finalConfig.itemIdField, lot[finalConfig.itemIdField])
          .eq('location_id', lot.location_id)
          .eq('is_active', true)
          .gt(qField, 0);

        const totalQty = (remainingItems || []).reduce((sum, i) => sum + (i[qField] || 0), 0);

        if (totalQty > 0) {
          await supabase
            .from(finalConfig.inventoryTable)
            .update({
              [qField]: totalQty,
              [finalConfig.lastUpdatedField || 'last_updated']: new Date().toISOString()
            })
            .eq('company_id', companyId)
            .eq(finalConfig.itemIdField, lot[finalConfig.itemIdField])
            .eq('location_id', lot.location_id);
        } else {
          await supabase
            .from(finalConfig.inventoryTable)
            .delete()
            .eq('company_id', companyId)
            .eq(finalConfig.itemIdField, lot[finalConfig.itemIdField])
            .eq('location_id', lot.location_id);
        }
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Error updating lot:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const uploadFile = async (fileType, file, transactionId) => {
    try {
      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));

      const bucketMap = {
        invoice:        finalConfig.storageBuckets?.invoices        || `${finalConfig.type}-transactions-invoices`,
        po:             finalConfig.storageBuckets?.po              || `${finalConfig.type}-transactions-po`,
        coa:            finalConfig.storageBuckets?.coa             || `${finalConfig.type}-transactions-coa`,
        sds:            finalConfig.storageBuckets?.sds             || `${finalConfig.type}-transactions-sds`,
        msds:           finalConfig.storageBuckets?.msds            || finalConfig.storageBuckets?.sds || `${finalConfig.type}-transactions-msds`,
        bol:            finalConfig.storageBuckets?.bol             || `${finalConfig.type}-transactions-bol`,
        production_doc: finalConfig.storageBuckets?.production_doc  || `${finalConfig.type}-transactions-production-documents`
      };

      const pathMap = {
        invoice: 'invoice_file_path',
        po: 'po_file_path',
        coa: 'coa_file_path',
        sds: 'sds_file_path',
        msds: 'msds_file_path',
        bol: 'bol_file_path',
        production_doc: 'production_doc_file_path'
      };

      const nameMap = {
        invoice: 'invoice_file_name',
        po: 'po_file_name',
        coa: 'coa_file_name',
        sds: 'sds_file_name',
        msds: 'msds_file_name',
        bol: 'bol_file_name',
        production_doc: 'production_doc_file_name'
      };

      const bucketName = bucketMap[fileType];
      const fileExt = file.name.split('.').pop();
      const fileName = `${companyId}/${transactionId}_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const updatePayload = {
        [pathMap[fileType]]: fileName,
        [nameMap[fileType]]: file.name
      };

      // production_doc and bol (for products) are stored on the items table
      if (isProduct && (fileType === 'production_doc' || fileType === 'bol')) {
        const { error: dbErr } = await supabase
          .from(finalConfig.itemsTable)
          .update(updatePayload)
          .eq('id', lot.id);
        if (dbErr) throw dbErr;
      } else {
        const { error: dbErr } = await supabase
          .from(finalConfig.transactionsTable)
          .update(updatePayload)
          .eq('id', transactionId);
        if (dbErr) throw dbErr;
      }
    } catch (err) {
      console.error(`Error uploading ${fileType}:`, err);
      throw err;
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
    }
  };
  
  const deleteFile = async (fileType, transactionId) => {
    try {
      const pathMap = {
        invoice: 'invoice_file_path',
        po: 'po_file_path',
        coa: 'coa_file_path',
        sds: 'sds_file_path',
        msds: 'msds_file_path',
        bol: 'bol_file_path',
        production_doc: 'production_doc_file_path'
      };

      const nameMap = {
        invoice: 'invoice_file_name',
        po: 'po_file_name',
        coa: 'coa_file_name',
        sds: 'sds_file_name',
        msds: 'msds_file_name',
        bol: 'bol_file_name',
        production_doc: 'production_doc_file_name'
      };
      
      const clearPayload = {
        [pathMap[fileType]]: null,
        [nameMap[fileType]]: null
      };

      // production_doc and bol (for products) are stored on the items table
      if (isProduct && (fileType === 'production_doc' || fileType === 'bol')) {
        await supabase
          .from(finalConfig.itemsTable)
          .update(clearPayload)
          .eq('id', lot.id);
      } else {
        await supabase
          .from(finalConfig.transactionsTable)
          .update(clearPayload)
          .eq('id', transactionId);
      }
    } catch (err) {
      console.error(`Error deleting ${fileType}:`, err);
      throw err;
    }
  };
  
  const handleClose = () => {
    setSelectedFiles({
      invoice: null,
      po: null,
      coa: null,
      sds: null,
      msds: null,
      bol: null,
      production_doc: null
    });
    setFilesToDelete({
      invoice: false,
      po: false,
      coa: false,
      sds: false,
      msds: false,
      bol: false,
      production_doc: false
    });
    setError(null);
    onClose();
  };
  
  if (!lot) return null;
  
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      '& fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.3),
        borderWidth: 1
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    },
    '& .MuiInputLabel-root': { fontWeight: 500 }
  };
  
  const allDocumentTypes = [
    { key: 'invoice', label: 'Invoice', icon: <ReceiptIcon fontSize="small" />, color: 'primary', customColor: null },
    { key: 'po', label: 'Purchase Order (PO)', icon: <DescriptionIcon fontSize="small" />, color: 'secondary', customColor: '#00897b' },
    { key: 'coa', label: 'Certificate of Analysis (COA)', icon: <AssignmentTurnedInIcon fontSize="small" />, color: 'success', customColor: null },
    { key: 'sds', label: 'Safety Data Sheet (SDS)', icon: <ScienceIcon fontSize="small" />, color: 'warning', customColor: null },
    { key: 'msds', label: 'Material Safety Data Sheet (MSDS)', icon: <ScienceIcon fontSize="small" />, color: 'warning', customColor: null },
    { key: 'bol', label: 'Bill of Lading (BOL)', icon: <LocalShippingIcon fontSize="small" />, color: 'info', customColor: null },
    { key: 'production_doc', label: 'Production Document', icon: <DescriptionIcon fontSize="small" />, color: 'info', customColor: null }
  ];

  // Filter to only show document types supported by this inventory type
  // Products use simple string arrays: ['invoice', 'po', 'coa', 'sds']
  // Others use object arrays: [{ type: 'invoice_file', key: 'invoice', ... }]
  const configDocTypes = finalConfig?.documentTypes;
  const documentTypes = (() => {
    if (!configDocTypes) return allDocumentTypes.filter(dt => dt.key !== 'sds');
    // Check if it's a string array (products) or object array (bottles, caps, etc.)
    if (typeof configDocTypes[0] === 'string') {
      return allDocumentTypes.filter(dt => configDocTypes.includes(dt.key));
    }
    // Object array — match by 'key' or by 'type' (strip '_file' suffix)
    const keys = configDocTypes.map(d => d.key || d.type?.replace('_file', ''));
    return allDocumentTypes.filter(dt => keys.includes(dt.key));
  })();
  
  const filteredVendors = suppliers.filter(v =>
    v.name?.toLowerCase().includes(vendorSearch.toLowerCase())
  );
  
  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 0,
        bgcolor: alpha(theme.palette.grey[100], 0.5),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
          <EditIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Edit Lot Information
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ m: 3, borderRadius: 2 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* Basic Information */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Paper sx={{ 
            borderRadius: 3, 
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            overflow: 'hidden',
            p: 4
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lot Number"
                  value={formData.lot_number || ''}
                  onChange={(e) => handleChange('lot_number', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Batch Number"
                  value={formData.batch_number || ''}
                  onChange={(e) => handleChange('batch_number', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
              
              {/* Vendor — hidden for products, shown for all other inventory types */}
              {!isProduct && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vendor"
                    value={selectedVendor ? selectedVendor.name : ''}
                    onClick={() => setVendorDialogOpen(true)}
                    placeholder="Select Vendor"
                    InputProps={{
                      readOnly: true,
                      sx: { cursor: 'pointer' }
                    }}
                    inputProps={{ style: { cursor: 'pointer' } }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiOutlinedInput-root': {
                        ...textFieldSx['& .MuiOutlinedInput-root'],
                        cursor: 'pointer',
                        '& input': { cursor: 'pointer' }
                      }
                    }}
                  />
                </Grid>
              )}

              {/* Product: Unit Config Selector + Pallet/Case/Unit fields */}
              {isProduct ? (
                <>
                  {/* Unit Configuration Selector */}
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Unit Configuration
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={openConfigDialog}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          minHeight: 48,
                          borderRadius: 2,
                          backgroundColor: 'white',
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                          color: 'text.primary',
                          fontWeight: 500,
                          px: 2,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': { borderColor: theme.palette.primary.main }
                        }}
                      >
                        {formData._configName
                          ? `${formData._configName} — ${formData.enable_cases ? `${formData.units_per_case} units/case` : 'Units only'}${formData.enable_pallets && formData.enable_cases ? `, ${formData.cases_per_pallet} cases/pallet` : ''}`
                          : `Current: ${formData.enable_cases ? `${formData.units_per_case} units/case` : 'Units only'}${formData.enable_pallets && formData.enable_cases ? `, ${formData.cases_per_pallet} cases/pallet` : ''} — Click to change`}
                      </Button>
                    </Box>
                  </Grid>

                  {formData.enable_pallets && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Pallets"
                        type="number"
                        value={formData.pallet_quantity ?? ''}
                        onChange={(e) => handleChange('pallet_quantity', e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        helperText={`1 pallet = ${formData.cases_per_pallet || 1} cases`}
                        sx={textFieldSx}
                      />
                    </Grid>
                  )}
                  {formData.enable_cases && (
                    <Grid item xs={12} sm={formData.enable_pallets ? 4 : 6}>
                      <TextField
                        fullWidth
                        label="Cases"
                        type="number"
                        value={formData.case_quantity ?? ''}
                        onChange={(e) => handleChange('case_quantity', e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        helperText={`1 case = ${formData.units_per_case || 1} units`}
                        sx={textFieldSx}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={formData.enable_pallets ? 4 : (formData.enable_cases ? 6 : 12)}>
                    <TextField
                      fullWidth
                      label="Units *"
                      type="number"
                      value={formData.unit_quantity ?? ''}
                      onChange={(e) => handleChange('unit_quantity', e.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      helperText="Individual units"
                      required
                      sx={textFieldSx}
                    />
                  </Grid>
                  {/* Calculated total for products */}
                  {(() => {
                    const pQ = parseInt(formData.pallet_quantity) || 0;
                    const cQ = parseInt(formData.case_quantity) || 0;
                    const uQ = parseInt(formData.unit_quantity) || 0;
                    const upc = formData.units_per_case || 1;
                    const cpp = formData.cases_per_pallet || 1;
                    const total = (pQ * cpp * upc) + (cQ * upc) + uQ;
                    return total > 0 ? (
                      <Grid item xs={12}>
                        <Box sx={{ px: 1.5, py: 0.75, bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                            Total: {total.toLocaleString()} base units
                          </Typography>
                        </Box>
                      </Grid>
                    ) : null;
                  })()}
                </>
              ) : (
                <>
                  {/* Chemical toggle: Enter by container vs by measure */}
                  {isChemical && lot._unitQtyPerUnit && lot._unitType && lot._unitMeasure && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                        <Button
                          size="small"
                          variant={quantityMode === 'container' ? 'contained' : 'outlined'}
                          onClick={() => setQuantityMode('container')}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                        >
                          Enter by {lot._unitType}
                        </Button>
                        <Button
                          size="small"
                          variant={quantityMode === 'measure' ? 'contained' : 'outlined'}
                          onClick={() => setQuantityMode('measure')}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                        >
                          Enter by {lot._unitMeasure}
                        </Button>
                      </Box>
                    </Grid>
                  )}

                  {/* Container mode (default) */}
                  {(!isChemical || quantityMode === 'container') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={lot._unitType ? `Quantity (${lot._unitType}) *` : 'Quantity *'}
                        type="number"
                        value={formData.current_quantity || ''}
                        onChange={(e) => handleChange('current_quantity', e.target.value)}
                        inputProps={{ min: 0, step: 0.001 }}
                        helperText={
                          lot._unitQtyPerUnit && lot._unitMeasure && lot._unitType
                            ? `${lot._unitQtyPerUnit}${lot._unitMeasure} per ${lot._unitType}`
                            : lot._unitType
                              ? `Enter quantity in ${lot._unitType}`
                              : 'Enter the quantity for this lot/batch'
                        }
                        required
                        sx={textFieldSx}
                      />
                      {lot._unitQtyPerUnit && lot._unitMeasure && formData.current_quantity && parseFloat(formData.current_quantity) > 0 && (
                        <Box sx={{ mt: 1, px: 1.5, py: 0.75, bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                            = {(parseFloat(formData.current_quantity) * parseFloat(lot._unitQtyPerUnit)).toFixed(3)} {lot._unitMeasure} total
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}

                  {/* Measure mode (chemical only) */}
                  {isChemical && quantityMode === 'measure' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Quantity (${lot._unitMeasure || 'measure'}) *`}
                        type="number"
                        value={formData.current_measure_quantity || ''}
                        onChange={(e) => handleChange('current_measure_quantity', e.target.value)}
                        inputProps={{ min: 0, step: 0.001 }}
                        helperText={lot._unitQtyPerUnit && lot._unitType ? `${lot._unitQtyPerUnit}${lot._unitMeasure} per ${lot._unitType}` : `Enter total in ${lot._unitMeasure}`}
                        required
                        sx={textFieldSx}
                      />
                      {lot._unitQtyPerUnit && formData.current_measure_quantity && parseFloat(formData.current_measure_quantity) > 0 && (
                        <Box sx={{ mt: 1, px: 1.5, py: 0.75, bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                            = {(parseFloat(formData.current_measure_quantity) / parseFloat(lot._unitQtyPerUnit)).toFixed(3)} {lot._unitType} total
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}
                </>
              )}
              
              {/* Invoice/PO fields — hidden for products, shown for all other inventory types */}
              {!isProduct && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Invoice Number"
                      value={formData.invoice_number || ''}
                      onChange={(e) => handleChange('invoice_number', e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Invoice Date"
                        value={formData.invoice_date ? dayjs(formData.invoice_date) : null}
                        onChange={(newValue) => {
                          handleChange('invoice_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: textFieldSx
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PO Number"
                      value={formData.po_number || ''}
                      onChange={(e) => handleChange('po_number', e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="PO Date"
                        value={formData.po_date ? dayjs(formData.po_date) : null}
                        onChange={(newValue) => {
                          handleChange('po_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: textFieldSx
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}

              {/* Manufacturing Date (products only) */}
              {isProduct && (
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Manufacturing Date"
                      value={formData.manufactured_date ? dayjs(formData.manufactured_date) : null}
                      onChange={(newValue) => {
                        handleChange('manufactured_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: textFieldSx
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              {/* Expiration Date (chemicals and products) */}
              {(finalConfig?.hasUnitOfMeasure || isProduct) && (
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Expiration Date"
                      value={formData.expiry_date ? dayjs(formData.expiry_date) : null}
                      onChange={(newValue) => {
                        handleChange('expiry_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: textFieldSx
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              {/* Shelf Life — auto-calculated from Mfg & Expiry dates (products only) */}
              {isProduct && (() => {
                const mfg = formData.manufactured_date;
                const exp = formData.expiry_date;
                if (!mfg || !exp) return null;
                const months = Math.round((new Date(exp) - new Date(mfg)) / (1000 * 60 * 60 * 24 * 30.44));
                if (months < 0) return null;
                return (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Shelf Life"
                      value={`${months} month${months !== 1 ? 's' : ''}`}
                      InputProps={{ readOnly: true }}
                      helperText="Auto-calculated from Manufacturing & Expiry dates"
                      sx={{
                        ...textFieldSx,
                        '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 }
                      }}
                    />
                  </Grid>
                );
              })()}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Document Upload Section */}
        {lot && (
          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center'
              }}>
                <DescriptionIcon sx={{ fontSize: 18, color: 'info.main' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Transaction Documents
              </Typography>
            </Box>
            
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
              overflow: 'hidden',
              p: 4
            }}>
              <Grid container spacing={3}>
                {documentTypes.map(({ key, label, icon, color, customColor }) => {
                  const filePathField = `${key}_file_path`;
                  const fileNameField = `${key}_file_name`;
                  const existingFile = lot.source_transaction?.[filePathField] || lot[filePathField];
                  const existingFileName = lot.source_transaction?.[fileNameField] || lot[fileNameField];
                  const bgColor = customColor ? customColor : theme.palette[color].main;
                  
                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                          {label}
                        </Typography>
                        
                        {/* Existing File */}
                        {existingFile && !selectedFiles[key] && !filesToDelete[key] && (
                          <Box sx={{ 
                            mb: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            p: 1, 
                            bgcolor: alpha(bgColor, 0.05), 
                            borderRadius: 1 
                          }}>
                            {React.cloneElement(icon, { sx: { fontSize: 20, color: customColor || `${color}.main` } })}
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {existingFileName || `${label}.pdf`}
                            </Typography>
                            <Tooltip title={canDelete ? `Delete ${label}` : 'Delete permission required'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocument(key)}
                                  disabled={!canDelete}
                                  sx={{ color: canDelete ? 'error.main' : 'text.disabled' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                        
                        {/* File to be deleted */}
                        {filesToDelete[key] && (
                          <Box sx={{ 
                            mb: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            p: 1, 
                            bgcolor: alpha(theme.palette.error.main, 0.1), 
                            borderRadius: 1 
                          }}>
                            {React.cloneElement(icon, { sx: { fontSize: 20, color: 'error.main' } })}
                            <Typography variant="body2" sx={{ flex: 1, textDecoration: 'line-through', color: 'text.secondary' }}>
                              {existingFileName || `${label}.pdf`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500 }}>
                              Will be deleted
                            </Typography>
                            <IconButton size="small" onClick={() => handleCancelDeleteDocument(key)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        
                        {/* Selected new file */}
                        {selectedFiles[key] && (
                          <Box sx={{ 
                            mb: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            p: 1, 
                            bgcolor: alpha(theme.palette.success.main, 0.1), 
                            borderRadius: 1 
                          }}>
                            {React.cloneElement(icon, { sx: { fontSize: 20, color: 'success.main' } })}
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {selectedFiles[key].name}
                            </Typography>
                            <IconButton size="small" onClick={() => removeSelectedFile(key)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        
                        {/* Upload button */}
                        <input
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          id={`${key}-upload`}
                          type="file"
                          onChange={(e) => handleFileSelect(e, key)}
                        />
                        <label htmlFor={`${key}-upload`}>
                          <Button
                            component="span"
                            variant="outlined"
                            size="small"
                            startIcon={uploadingFiles[key] ? <CircularProgress size={16} /> : <UploadIcon />}
                            disabled={uploadingFiles[key]}
                            fullWidth
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                              '&:hover': {
                                border: `1px dashed ${theme.palette.primary.main}`,
                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                              }
                            }}
                          >
                            {selectedFiles[key] ? `Replace ${label}` : `Upload ${label}`}
                          </Button>
                        </label>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2.5, 
        bgcolor: alpha(theme.palette.grey[100], 0.3),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (isProduct ? false : (!formData.lot_number || !formData.batch_number || !formData.current_quantity))}
          startIcon={loading && <CircularProgress size={16} />}
          sx={{
            borderRadius: 2,
            px: 4,
            textTransform: 'none',
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
            },
            '&.Mui-disabled': {
              bgcolor: alpha(theme.palette.grey[400], 0.5),
              color: alpha(theme.palette.text.primary, 0.4)
            }
          }}
        >
          {loading ? 'Updating...' : 'Update Lot Information'}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Unit Config Selection Dialog (products only) */}
    {isProduct && (
      <ConfigSelectionDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        configs={availableConfigs}
        search={configSearch}
        onSearchChange={setConfigSearch}
        onSelect={handleConfigSelect}
        currentConfigId={formData._configId || null}
      />
    )}

    {/* Vendor Search Dialog */}
    <Dialog
      open={vendorDialogOpen}
      onClose={() => setVendorDialogOpen(false)}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          height: '80vh',
          maxHeight: '80vh',
          margin: 'auto',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
          Select Vendor
        </Typography>
        <IconButton onClick={() => setVendorDialogOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <TextField
            fullWidth
            placeholder="Search vendors..."
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' }
              }
            }}
          />
        </Box>
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No vendors found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => (
                  <TableRow 
                    key={vendor.id}
                    hover 
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {vendor.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleVendorSelect(vendor)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: 'white',
                          color: 'text.primary',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: 'white',
                            color: 'text.primary'
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
}
